import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { VeriffService } from "../../../services/veriff/veriff.service";

export function VeriffPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const [linkDoc, setLinkDoc] = useState(null);
  const [veriffStatus, setVeriffStatus] = useState("idle"); // 'idle' | 'loading_session' | 'ready' | 'error' | 'submitted'
  const [errorMsg, setErrorMsg] = useState("");

  // Dynamically load the Veriff In-Context SDK CDN script on mount
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.veriff.me/incontext/js/v1/veriff.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      try {
        document.body.removeChild(script);
      } catch (e) {
        console.warn("Could not remove Veriff SDK script", e);
      }
    };
  }, []);

  // Valida el enlace público y obtiene el documento asociado al token
  useEffect(() => {
    const validateLink = async () => {
      if (!token) {
        setLinkInvalid(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await VeriffService.validateLink(token);
        const doc = response?.data?.doc;
        if (response?.success !== true || !doc) {
          setLinkInvalid(true);
          setIsLoading(false);
          return;
        }
        console.log("Documento obtenido del enlace:", doc);
        setLinkDoc(doc);
        setIsLoading(false);
      } catch (error) {
        console.error("Error validando el enlace de verificación:", error);
        setLinkInvalid(true);
        setIsLoading(false);
      }
    };

    validateLink();
  }, [token]);

  const handleStartVerification = async () => {
    setVeriffStatus("loading_session");
    setErrorMsg("");
    try {
      const response = await VeriffService.createSessionByLink(token);
      if (response?.success !== true || !response?.data?.verification?.url) {
        throw new Error(response?.message || "No se pudo iniciar la sesión de verificación.");
      }
      const sessionUrl = response.data.verification.url;

      // Use Veriff InContext SDK directly to open the modal overlay
      if (window.veriffSDK) {
        window.veriffSDK.createVeriffFrame({
          url: sessionUrl,
          onEvent: function (msg) {
            console.log("Veriff event received:", msg);
            if (msg === "FINISHED" || msg === "SUBMITTED") {
              navigate(`/veriff-status/${token}`);
            } else if (msg === "CANCELED") {
              setVeriffStatus("idle");
            }
          }
        });
        setVeriffStatus("ready");
      } else {
        throw new Error("El SDK de Veriff no cargó correctamente. Por favor recarga la página.");
      }
    } catch (error) {
      console.error("Error initiating Veriff:", error);

      // El token pudo vencer entre que se cargó la página y que se dio clic (ventana de 15 min)
      if (error?.response?.status === 401) {
        setLinkInvalid(true);
        return;
      }

      setVeriffStatus("error");
      setErrorMsg(
        error.response?.data?.message ||
        error.message ||
        "No se pudo iniciar la sesión de verificación. Inténtalo de nuevo."
      );
    }
  };

  return (
    <div className="bg-purpleLight min-h-screen pt-32 pb-10">
      <div className="max-w-xl mx-auto px-4">
        <div className="shadow-lg overflow-hidden rounded-md">
          <div className="px-4 py-8 bg-white sm:p-8 text-center">
            {linkInvalid ? (
              <>
                <h2 className="text-2xl font-bold mb-4 text-tdark">
                  Enlace inválido o caducado
                </h2>
                <p className="text-grayP">
                  Este enlace de verificación ya no es válido. Por favor solicita uno nuevo.
                </p>
              </>
            ) : isLoading ? (
              <div className="flex flex-col items-center justify-center min-h-[200px]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-darkBg mb-4"></div>
                <p className="text-grayP">Validando enlace...</p>
              </div>
            ) : (
              <>
                <div className="flex justify-center mb-6">
                  <img
                    src="/svg/imagotipo-galilea-20.svg"
                    alt="Galilea Logo"
                    className="h-16 w-auto"
                  />
                </div>

                {linkDoc && (
                  <p className="text-grayP mb-2">
                    Documento: <span className="font-bold text-tdark">{linkDoc}</span>
                  </p>
                )}

                <p className="text-grayP mb-8">
                  Para garantizar la seguridad de tus transacciones y cumplir con las regulaciones vigentes, Galilea utiliza Veriff para realizar una verificación de identidad rápida y segura. Necesitarás tu documento de identidad y un dispositivo con cámara.
                </p>

                {veriffStatus === "idle" && (
                  <button
                    onClick={handleStartVerification}
                    className="w-full py-4 px-6 bg-darkBg text-white rounded-xl font-bold text-lg hover:bg-darkBg/90 transition duration-300 shadow-md cursor-pointer"
                  >
                    Iniciar Verificación
                  </button>
                )}

                {veriffStatus === "loading_session" && (
                  <div className="flex flex-col items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-darkBg mb-4"></div>
                    <p className="text-grayP">Iniciando sesión de verificación...</p>
                  </div>
                )}

                {veriffStatus === "ready" && (
                  <div className="py-4">
                    <p className="text-green-600 font-bold mb-2">Flujo de Veriff iniciado</p>
                    <p className="text-grayP text-sm">Por favor, completa los pasos en la ventana emergente de Veriff.</p>
                    <button
                      onClick={() => setVeriffStatus("idle")}
                      className="mt-6 text-sm text-grayP underline hover:text-tdark cursor-pointer"
                    >
                      Volver a intentar
                    </button>
                  </div>
                )}

                {veriffStatus === "error" && (
                  <div className="py-4">
                    <h3 className="text-lg font-bold text-red-600 mb-2">
                      Error de Verificación
                    </h3>
                    <p className="text-red-500 mb-6 text-sm">{errorMsg}</p>
                    <button
                      onClick={handleStartVerification}
                      className="w-full py-3 px-6 bg-darkBg text-white rounded-xl font-bold hover:bg-darkBg/90 transition cursor-pointer"
                    >
                      Reintentar
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
